import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import i18next from 'i18next';
import { Url } from '@wix/da-url';
import {
  reduxModuleLoaded,
  subscribeToTier,
  unsubRequest,
  unsubRequestByUsername,
  changeTier,
  pushChangeTierModal,
} from './actions';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import { pushModal, replaceModal, popModal } from '../../../Modals/redux/basicActions';
import { ModalType } from '../../../Modals/redux/types';
import { getCurrentUser } from '../../../publicSession/selectors';

export function fakeUnsubscribeResponse(options: any): any {
  if (process.env.NODE_ENV !== 'production') {
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    return { success: true };
  }
}

export function fakeCheckUpgradeResponse(options: any): any {
  if (process.env.NODE_ENV !== 'production') {
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }
    return {};
  }
}

function* handleSubscribeToTier({ payload: { tier } }: ReturnType<typeof subscribeToTier>) {
  // Step 0: Check that we're not the author
  const currentUser = yield select(getCurrentUser);
  if (currentUser.username === tier.author?.username) {
    yield put(
      pushModal(ModalType.CONFIRMATION, {
        params: {
          withCancelButton: false,
          title: i18next.t('deviation.tier.changePopup.selfSubFailure.title'),
          text: i18next.t('deviation.tier.changePopup.selfSubFailure.text'),
          confirmBtnLabel: i18next.t('deviation.tier.changePopup.selfSubFailure.accept'),
        },
      })
    );
    return;
  }

  // Step 1: Check if we need to unsubscribe from an existing tier
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/tier/check_upgrade',
      params: {
        tierid: tier.tier?.tierId,
      },
    },
    fakeCheckUpgradeResponse
  );
  if (isApiError(response)) {
    yield put(
      pushModal(ModalType.CONFIRMATION, {
        params: {
          withCancelButton: false,
          title: i18next.t('deviation.tier.changePopup.failure.title'),
          text: i18next.t('deviation.tier.changePopup.failure.body'),
          confirmBtnLabel: i18next.t('deviation.tier.changePopup.failure.accept'),
        },
      })
    );
    return;
  }

  if (response?.currentTier) {
    yield put(pushChangeTierModal(response?.currentTier, tier, response?.isDowngrade));
  } else {
    if (tier.tier?.subproductId) {
      window.location.href = Url.checkoutLink(tier.tier?.subproductId);
    }
  }
}

function* handleUnsubRequest({ payload: { tier } }: ReturnType<typeof unsubRequest>) {
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/tier/unsubscribe',
      data: {
        tierid: tier.tier?.tierId,
      },
    },
    fakeUnsubscribeResponse
  );
  yield put(popModal());
  if (isApiError(response)) {
    yield put(
      pushModal(ModalType.CONFIRMATION, {
        params: {
          withCancelButton: false,
          title: i18next.t('deviation.tier.unsubPopup.failure.title'),
          text: i18next.t('deviation.tier.unsubPopup.failure.body'),
          confirmBtnLabel: i18next.t('deviation.tier.unsubPopup.failure.accept'),
        },
      })
    );
    return;
  }
  const reloadPage = () => {
    window.location.reload();
  };
  yield put(
    pushModal(ModalType.CONFIRMATION, {
      params: {
        withCancelButton: false,
        title: i18next.t('deviation.tier.unsubPopup.success.title'),
        text: i18next.t('deviation.tier.unsubPopup.success.body', {
          tierName: tier.title,
        }),
        confirmBtnLabel: i18next.t('deviation.tier.unsubPopup.success.accept'),

        // Refresh any stale state
        onConfirm: reloadPage,
        onCancel: reloadPage,
      },
    })
  );
}

function* handleUnsubRequestByUsername({
  payload: { username },
}: ReturnType<typeof unsubRequestByUsername>) {
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/tier/user/unsubscribe',
      data: {
        subscribed_to_username: username,
      },
    },
    fakeUnsubscribeResponse
  );
  if (isApiError(response)) {
    yield put(
      replaceModal(ModalType.CONFIRMATION, {
        params: {
          withCancelButton: false,
          title: i18next.t('deviation.tier.unsubPopup.failure.title'),
          text: i18next.t('deviation.tier.unsubPopup.failure.body'),
          confirmBtnLabel: i18next.t('deviation.tier.unsubPopup.failure.accept'),
        },
      })
    );
    return;
  }

  const redirectToSubscriptions = () => {
    window.location.href = Url.userSubscriptionsPath({
      username,
    });
  };
  // We have to use replaceModal to keep the <TierConsumerReduxModule />
  // loaded and keep this saga active until the final confirmation modal is shown
  yield put(
    replaceModal(ModalType.CONFIRMATION, {
      params: {
        withCancelButton: false,
        title: i18next.t('deviation.tier.unsubPopup.success.title'),
        text: i18next.t('deviation.tier.unsubPopup.success.body.generic'),
        confirmBtnLabel: i18next.t('deviation.tier.unsubPopup.success.accept'),

        // Refresh any stale state
        onConfirm: redirectToSubscriptions,
        onCancel: redirectToSubscriptions,
      },
    })
  );
}

function* handleChangeTier({ payload: { currentTier, newTier } }: ReturnType<typeof changeTier>) {
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/tier/unsubscribe',
      data: {
        tierid: currentTier.tier?.tierId,
      },
    },
    fakeUnsubscribeResponse
  );
  if (isApiError(response)) {
    yield put(
      pushModal(ModalType.CONFIRMATION, {
        params: {
          withCancelButton: false,
          title: i18next.t('deviation.tier.changePopup.failure.title'),
          text: i18next.t('deviation.tier.changePopup.failure.body'),
          confirmBtnLabel: i18next.t('deviation.tier.changePopup.failure.accept'),
        },
      })
    );
    return;
  }

  if (newTier.tier?.subproductId) {
    window.location.href = Url.checkoutLink(newTier.tier?.subproductId);
  }
}

export default function* subscribeSaga() {
  yield take(reduxModuleLoaded);
  yield all([
    takeEvery(subscribeToTier, handleSubscribeToTier),
    takeEvery(unsubRequest, handleUnsubRequest),
    takeEvery(unsubRequestByUsername, handleUnsubRequestByUsername),
    takeEvery(changeTier, handleChangeTier),
  ]);
}
